import { type CSSProperties, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import type { CommentData } from '../../types';

const ALLOWED_CHARACTERS = 1000;
const MAX_TEXTAREA_HEIGHT = 300;

interface Props {
  comment: CommentData;
  onSave: (commentId: string, newContent: string) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  translations?: Record<string, string>;
  textareaBorderColor?: string;
  textareaBorderRadius?: string;
  textareaPadding?: string;
  textareaTextColor?: string;
  saveButtonBackgroundColor?: string;
  saveButtonTextColor?: string;
  cancelButtonBackgroundColor?: string;
  cancelButtonTextColor?: string;
}

export function InlineEditForm({
  comment,
  onSave,
  onCancel,
  isSubmitting = false,
  translations,
  textareaBorderColor = '#e5e7eb',
  textareaBorderRadius = '6px',
  textareaPadding = '12px',
  textareaTextColor,
  saveButtonBackgroundColor,
  saveButtonTextColor,
  cancelButtonBackgroundColor,
  cancelButtonTextColor,
}: Props) {
  const [content, setContent] = useState(comment.content);
  const charCount = content.length;
  const charactersRemaining = ALLOWED_CHARACTERS - charCount;
  const isOverLimit = charactersRemaining < 0;
  const hasChanges = content !== comment.content;
  const canSave = !isOverLimit && content.trim().length > 0 && hasChanges && !isSubmitting;

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const textAreaComment = textAreaRef.current;

    const onInput = (event: Event) => {
      const textarea = event.target as HTMLTextAreaElement;
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT);
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
    };

    if (textAreaComment) {
      textAreaComment.style.height = 'auto';
      const initialHeight = Math.min(textAreaComment.scrollHeight, MAX_TEXTAREA_HEIGHT);
      textAreaComment.style.height = `${initialHeight}px`;
      textAreaComment.style.overflowY = textAreaComment.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
      textAreaComment.addEventListener('input', onInput, false);
      textAreaComment.focus();
      // Move cursor to end
      textAreaComment.setSelectionRange(textAreaComment.value.length, textAreaComment.value.length);
    }

    return () => {
      if (textAreaComment) {
        textAreaComment.removeEventListener('input', onInput, false);
      }
    };
  }, [textAreaRef]);

  const handleSave = () => {
    if (canSave) {
      onSave(comment.id, content);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      onCancel();
    }
    // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) to save
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSave) {
      e.preventDefault();
      onSave(comment.id, content);
    }
  };

  return (
    <div className="flex flex-col gap-y-2 w-full">
      <div
        className={classNames(
          'w-full relative overflow-hidden focus-within:ring-1',
          isOverLimit
            ? 'focus-within:border-red-600 focus-within:ring-red-600'
            : 'focus-within:border-gray-200 focus-within:ring-gray-200',
        )}
        style={{
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: isOverLimit ? '#dc2626' : textareaBorderColor,
          borderRadius: textareaBorderRadius,
        }}
      >
        <textarea
          ref={textAreaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Edit comment"
          aria-describedby="edit-char-count"
          aria-invalid={isOverLimit}
          className="text-sm block w-full h-full min-h-[92px] resize-none border-0 bg-transparent focus:ring-0"
          style={
            {
              padding: textareaPadding,
              color: textareaTextColor,
              maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
            } as CSSProperties
          }
        />
      </div>

      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col gap-y-1">
          <span
            id="edit-char-count"
            aria-live="polite"
            className={classNames('text-xs', isOverLimit ? 'text-red-600' : 'opacity-60')}
          >
            {charCount}/{ALLOWED_CHARACTERS}
          </span>
          {isOverLimit && <span className="text-xs text-red-600">Comment exceeds character limit</span>}
        </div>
        <div className="flex flex-row gap-x-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={classNames(
              'px-[9px] py-[5px] text-sm rounded-md shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
              isSubmitting && 'opacity-50 cursor-not-allowed',
            )}
            style={{
              backgroundColor: cancelButtonBackgroundColor || 'transparent',
              color: cancelButtonTextColor,
            }}
          >
            {translations?.['shared.cta.cancel'] || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className={classNames(
              'px-[9px] py-[5px] text-sm rounded-md shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
              !canSave && 'opacity-50 cursor-not-allowed',
            )}
            style={{
              backgroundColor: saveButtonBackgroundColor,
              color: saveButtonTextColor,
            }}
          >
            {isSubmitting
              ? translations?.['comments.editing.saving'] || 'Saving...'
              : translations?.['comments.editing.save'] || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
